import { useEffect, useState } from "react";

import { fetchCinemas, fetchSchedule } from "./api";
import { CinemaPicker } from "./components/CinemaPicker";
import { FilmDialog } from "./components/FilmDialog";
import { FilmList } from "./components/FilmList";
import { HallsDialog } from "./components/HallsDialog";
import { PlanDialog } from "./components/PlanDialog";
import { ResultsDialog } from "./components/ResultsDialog";
import { SearchWizard } from "./components/SearchWizard";
import { formatDay } from "./format";
import type { Criteria } from "./search";
import type { Cinema, Movie, Schedule, SearchResponse, Showtime } from "./types";

const STORAGE_KEY = "bonne-place:cinema";
const DEFAULT_CINEMA = "cinema-pathe-odysseum";

type Overlay =
  | { kind: "cinemas" }
  | { kind: "film"; movie: Movie }
  | { kind: "plan"; movie: Movie; showtime: Showtime }
  | { kind: "halls" }
  | { kind: "search" }
  | { kind: "results"; response: SearchResponse; criteria: Criteria }
  | null;

export default function App() {
  const [cinemas, setCinemas] = useState<Cinema[]>([]);
  const [cinema, setCinema] = useState<Cinema | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<Overlay>(null);

  useEffect(() => {
    fetchCinemas()
      .then((list) => {
        setCinemas(list);
        const wanted = localStorage.getItem(STORAGE_KEY) ?? DEFAULT_CINEMA;
        setCinema(list.find((item) => item.slug === wanted) ?? list[0]);
      })
      .catch((cause: Error) => {
        setError(cause.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!cinema) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchSchedule(cinema.slug, date ?? undefined)
      .then((result) => {
        if (cancelled) return;
        setSchedule(result);
        setDate(result.date);
      })
      .catch((cause: Error) => {
        if (!cancelled) {
          setSchedule(null);
          setError(cause.message);
        }
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [cinema, date]);

  const selectCinema = (next: Cinema) => {
    localStorage.setItem(STORAGE_KEY, next.slug);
    setCinema(next);
    setDate(null);
    setSchedule(null);
    setOverlay(null);
  };

  const retry = () => {
    setError(null);
    setDate((current) => current);
    if (cinema) setCinema({ ...cinema });
  };

  const movies = schedule?.movies ?? [];
  const seances = movies.reduce((total, movie) => total + movie.showtimes.length, 0);

  if (!cinema && loading) {
    return (
      <div className="page splash">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="lamp" />
          <span className="brand-name">La Bonne Place</span>
        </div>
        <span className="state">chargement de la programmation…</span>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="masthead">
        <div className="masthead-inner">
          <div className="brand">
            <span className="brand-lamp" />
            <span className="brand-name">La Bonne Place</span>
          </div>
          <span className="brand-tag">
            choisir la salle,
            <br />
            pas juste l'heure
          </span>

          {cinema && (
            <button
              type="button"
              className="cinema-button"
              onClick={() => setOverlay({ kind: "cinemas" })}
            >
              <span>
                <span className="cinema-button-name">{cinema.name}</span>
                <span className="cinema-button-meta">
                  {cinema.city} · {cinema.hallCount} salles · {cinema.seatCount} places
                </span>
              </span>
              <span className="cinema-button-cue">changer ⌄</span>
            </button>
          )}
        </div>
      </header>

      <main className="shell">
        {error && (
          <div className="errorbox">
            <div className="errorbox-mark">!</div>
            <p className="empty-title">Programmation indisponible</p>
            <code>{error}</code>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn btn-ghost" onClick={retry}>
                Réessayer
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setOverlay({ kind: "cinemas" })}
              >
                Changer de cinéma
              </button>
            </div>
          </div>
        )}

        {!error && schedule && (
          <nav className="datestrip" aria-label="Jours à l'affiche">
            {schedule.dates.map((day) => (
              <button
                key={day}
                type="button"
                className={day === schedule.date ? "day is-current" : "day"}
                aria-current={day === schedule.date}
                onClick={() => setDate(day)}
              >
                <span className="day-name">{formatDay(day, { weekday: "short" })}</span>
                <span className="day-number">{formatDay(day, { day: "numeric" })}</span>
                <span className="day-month">{formatDay(day, { month: "short" })}</span>
              </button>
            ))}
          </nav>
        )}

        {!error && schedule && (
          <div className="dayhead">
            <h1 className="dayhead-title">
              {formatDay(schedule.date, { weekday: "long", day: "numeric", month: "long" })}
            </h1>
            <span className="dayhead-count">
              {movies.length} film{movies.length > 1 ? "s" : ""} · {seances} séance
              {seances > 1 ? "s" : ""}
            </span>

            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setOverlay({ kind: "halls" })}
            >
              Les salles
            </button>

            {movies.length > 0 && (
              <button
                type="button"
                className="search-cta"
                onClick={() => setOverlay({ kind: "search" })}
              >
                <span className="search-cta-badge">✦</span>
                <span>
                  <span className="search-cta-label">Trouver la meilleure place</span>
                  <span className="search-cta-sub">un assistant vous guide en quelques étapes</span>
                </span>
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className="spinner" />
            <span className="state">lecture des séances…</span>
          </div>
        )}

        {!loading && !error && movies.length === 0 && (
          <div className="empty">
            <p className="empty-title">Aucune séance ce jour-là</p>
            <p className="empty-text">Ce cinéma ne programme pas cette date. Choisissez un autre jour.</p>
          </div>
        )}

        {!loading && movies.length > 0 && (
          <FilmList
            movies={movies}
            onOpenMovie={(movie) => setOverlay({ kind: "film", movie })}
            onOpenShowtime={(movie, showtime) => setOverlay({ kind: "plan", movie, showtime })}
          />
        )}

        <p className="footnote">
          Le remplissage est un instantané : une séance peut se remplir entre l'affichage et la
          réservation. Données publiques pathe.fr · projet non officiel, sans lien avec Pathé.
        </p>
      </main>

      {overlay?.kind === "cinemas" && (
        <CinemaPicker
          cinemas={cinemas}
          current={cinema}
          onSelect={selectCinema}
          onClose={() => setOverlay(null)}
        />
      )}

      {overlay?.kind === "halls" && cinema && schedule && (
        <HallsDialog cinema={cinema} date={schedule.date} onClose={() => setOverlay(null)} />
      )}

      {overlay?.kind === "film" && cinema && schedule && (
        <FilmDialog
          cinemaSlug={cinema.slug}
          date={schedule.date}
          movie={overlay.movie}
          onOpenShowtime={(showtime) =>
            setOverlay({ kind: "plan", movie: overlay.movie, showtime })
          }
          onClose={() => setOverlay(null)}
        />
      )}

      {overlay?.kind === "plan" && (
        <PlanDialog
          movie={overlay.movie}
          showtime={overlay.showtime}
          onClose={() => setOverlay(null)}
        />
      )}

      {overlay?.kind === "search" && cinema && schedule && movies.length > 0 && (
        <SearchWizard
          cinemaSlug={cinema.slug}
          date={schedule.date}
          movies={movies}
          initialMovieSlug={movies[0].slug}
          onResults={(response, criteria) => setOverlay({ kind: "results", response, criteria })}
          onClose={() => setOverlay(null)}
        />
      )}

      {overlay?.kind === "results" && (
        <ResultsDialog
          response={overlay.response}
          movieTitle={
            movies.find((movie) => movie.slug === overlay.criteria.movieSlug)?.title ?? ""
          }
          onAdjust={() => setOverlay({ kind: "search" })}
          onClose={() => setOverlay(null)}
        />
      )}
    </div>
  );
}
